import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { PrismBanner, PrismButton, PrismDialog, PrismInputText, PrismInputTextarea, PrismSelect } from "@prism-ui/react";
import { toast } from "../../../Toaster/toast";
import { apiError } from "./RatingWindowTab";
import useConfirm from "../../../../hooks/useConfirm";
import {
  HUB_STATUSES,
  HUB_TRACKS,
  UNDECIDED_STATUS_IDS,
  downloadApplications,
  managerApprovalLabel,
  placementNote,
  ratingCategory,
  statusKeyOf,
  type HubApplication,
  type ReviewPlacement,
  type TrackKey,
} from "./hub";

export interface PendingTransfer {
  id: number;
  application_id: number;
  from_chapter_id: number;
  to_chapter_id: number;
  from_chapter: string;
  to_chapter: string;
  applicant_name: string | null;
  applicant_type_id: number;
  requested_by_name: string | null;
  note: string | null;
  created_at: string;
}

interface Chapter {
  id: number;
  location: string;
}

interface Props {
  chapterId: number;
  chapterName: string;
  year: number;
  // Opens Host Review, where late arrivals are decided.
  onOpenHostReview?: () => void;
}

const ALL = "all";
const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
const trackLabel = (typeId: number) => HUB_TRACKS.find((t) => t.typeId === typeId)?.singular ?? "Unknown";
const nameOf = (a: { name?: string | null; applicant_name?: string | null }) =>
  a.name ?? a.applicant_name ?? "Unknown applicant";

const ApplicationsTab = ({ chapterId, chapterName, year, onOpenHostReview }: Props) => {
  const [rows, setRows] = useState<HubApplication[] | null>(null);
  const [transfers, setTransfers] = useState<{ incoming: PendingTransfer[]; outgoing: PendingTransfer[] }>({
    incoming: [],
    outgoing: [],
  });
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [track, setTrack] = useState<TrackKey | typeof ALL>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [search, setSearch] = useState("");
  const [managing, setManaging] = useState<HubApplication | null>(null);
  const [typeId, setTypeId] = useState("");
  const [destination, setDestination] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const params = { chapter_id: chapterId, year };
      const [list, pending] = await Promise.all([
        axios.get<HubApplication[]>("/api/applications_hub/applications", { params }),
        axios.get("/api/applications_hub/transfers", { params: { chapter_id: chapterId } }),
      ]);
      setRows(list.data);
      setTransfers(pending.data);
    } catch (err) {
      toast.error(apiError(err, "Unable to load applications"));
    }
  }, [chapterId, year]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    axios
      .get<Chapter[]>("/api/chapters")
      .then((r) => setChapters(r.data))
      .catch(() => setChapters([]));
  }, []);

  const trackCounts = useMemo(() => {
    const counts: Record<string, number> = { [ALL]: rows?.length ?? 0 };
    HUB_TRACKS.forEach((t) => (counts[t.key] = rows?.filter((r) => r.track === t.key).length ?? 0));
    return counts;
  }, [rows]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (rows ?? []).filter(
      (r) =>
        (track === ALL || r.track === track) &&
        (status === ALL || statusKeyOf(r.status_id) === status) &&
        (!needle || `${r.name ?? ""} ${r.email ?? ""}`.toLowerCase().includes(needle))
    );
  }, [rows, track, status, search]);

  const openManage = (row: HubApplication) => {
    setManaging(row);
    setTypeId(String(row.applicant_type_id));
    setDestination("");
    setNote("");
  };

  const exportRows = async () => {
    setExporting(true);
    try {
      await downloadApplications(visible, chapterName);
    } catch (err) {
      toast.error(apiError(err, "Unable to export applications"));
    } finally {
      setExporting(false);
    }
  };

  const changeType = async () => {
    if (!managing) return;
    const next = Number(typeId);
    const ok = await confirm({
      title: "Change applicant type?",
      message: `Move ${nameOf(managing)} from ${trackLabel(managing.applicant_type_id)} to ${trackLabel(next)}? Existing ratings stay on the application.`,
      confirmLabel: "Change type",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await axios.patch<{ review?: ReviewPlacement | null }>(
        `/api/applications_hub/applications/${managing.id}/type`,
        { applicant_type_id: next },
        { params: { chapter_id: chapterId } }
      );
      toast.success(`${nameOf(managing)} is now a ${trackLabel(next).toLowerCase()} applicant.${placementNote(data?.review)}`);
      setManaging(null);
      await load();
    } catch (err) {
      toast.error(apiError(err, "Unable to change the applicant type"));
    } finally {
      setBusy(false);
    }
  };

  const proposeTransfer = async () => {
    if (!managing || !destination) return;
    const to = chapters.find((c) => c.id === Number(destination));
    const ok = await confirm({
      title: "Propose a chapter transfer?",
      message: `Ask ${to?.location ?? "the other chapter"} to take ${nameOf(managing)}'s application? It moves once one of their officers accepts.`,
      confirmLabel: "Propose transfer",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await axios.post(
        `/api/applications_hub/applications/${managing.id}/transfer`,
        { to_chapter_id: Number(destination), note: note.trim() || undefined },
        { params: { chapter_id: chapterId } }
      );
      toast.success(`Transfer to ${to?.location ?? "the other chapter"} proposed.`);
      setManaging(null);
      await load();
    } catch (err) {
      toast.error(apiError(err, "Unable to propose the transfer"));
    } finally {
      setBusy(false);
    }
  };

  const decideTransfer = async (t: PendingTransfer, action: "accept" | "decline" | "cancel") => {
    const copy = {
      accept: {
        title: "Accept transfer?",
        message: `Move ${nameOf(t)}'s application from ${t.from_chapter} into ${t.to_chapter}? Their ratings come with it.`,
        label: "Accept transfer",
        tone: "primary" as const,
        done: `${nameOf(t)} moved to ${t.to_chapter}.`,
      },
      decline: {
        title: "Decline transfer?",
        message: `Keep ${nameOf(t)}'s application in ${t.from_chapter}?`,
        label: "Decline transfer",
        tone: "danger" as const,
        done: "Transfer declined.",
      },
      cancel: {
        title: "Cancel transfer?",
        message: `Withdraw the request to move ${nameOf(t)} to ${t.to_chapter}?`,
        label: "Cancel transfer",
        tone: "danger" as const,
        done: "Transfer cancelled.",
      },
    }[action];
    const ok = await confirm({ title: copy.title, message: copy.message, confirmLabel: copy.label, tone: copy.tone });
    if (!ok) return;
    try {
      const { data } = await axios.patch<{ review?: ReviewPlacement | null }>(
        `/api/applications_hub/transfers/${t.id}/${action}`,
        {},
        { params: { chapter_id: chapterId } }
      );
      toast.success(`${copy.done}${action === "accept" ? placementNote(data?.review) : ""}`);
      await load();
    } catch (err) {
      toast.error(apiError(err, `Unable to ${action} the transfer`));
    }
  };

  if (!rows) return <p>Loading…</p>;

  const undecided = managing ? UNDECIDED_STATUS_IDS.includes(managing.status_id) : false;
  const statusOptions = [
    { value: ALL, display: "All statuses" },
    ...HUB_STATUSES.map((s) => ({ value: s.key, display: s.label })),
  ];
  const typeOptions = HUB_TRACKS.map((t) => ({ value: String(t.typeId), display: t.singular }));
  const destinationOptions = [
    { value: "", display: "Choose a chapter" },
    ...chapters
      .filter((c) => c.id !== 0 && c.id !== chapterId)
      .map((c) => ({ value: String(c.id), display: c.location })),
  ];

  return (
    <div className="hub-applications">
      <PrismBanner tone="info">
        Every application in this chapter for the year, by track. Search, filter by status, or open an application;
        the rows shown can be exported to XLSX from the bottom of the page. Use Manage to change an undecided application&apos;s type or propose moving it to
        another chapter; accepting and rejecting happen in Host Review.
      </PrismBanner>
      <div className="waitlist-heading">
        <span className="host-review-badge primary">{year} cohort</span>
        <span className="waitlist-heading-note">Change the year on the Overview tab.</span>
      </div>
      {(transfers.incoming.length > 0 || transfers.outgoing.length > 0) && (
        <section className="hub-transfers" aria-label="Pending chapter transfers">
          <h3>Pending chapter transfers</h3>
          <ul>
            {transfers.incoming.map((t) => (
              <li key={t.id}>
                <span>
                  <strong>{nameOf(t)}</strong> ({trackLabel(t.applicant_type_id).toLowerCase()}) from {t.from_chapter}
                  {t.requested_by_name && <>, proposed by {t.requested_by_name}</>}
                  {t.note && <q>{t.note}</q>}
                </span>
                <span className="hub-transfer-actions">
                  <PrismButton size="sm" onPrismClick={() => decideTransfer(t, "accept")}>
                    Accept
                  </PrismButton>
                  <PrismButton size="sm" tone="secondary" display="outline" onPrismClick={() => decideTransfer(t, "decline")}>
                    Decline
                  </PrismButton>
                </span>
              </li>
            ))}
            {transfers.outgoing.map((t) => (
              <li key={t.id}>
                <span>
                  <strong>{nameOf(t)}</strong> waiting on {t.to_chapter}
                  {t.note && <q>{t.note}</q>}
                </span>
                <span className="hub-transfer-actions">
                  <PrismButton size="sm" tone="secondary" display="outline" onPrismClick={() => decideTransfer(t, "cancel")}>
                    Cancel request
                  </PrismButton>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="waitlist-tracks" role="tablist" aria-label="Track">
        {[{ key: ALL, label: "All" }, ...HUB_TRACKS].map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === track}
            className={t.key === track ? "active" : ""}
            onClick={() => setTrack(t.key as TrackKey | typeof ALL)}
          >
            {t.label}
            <span className="waitlist-track-count">{trackCounts[t.key]}</span>
          </button>
        ))}
      </div>

      <div className="hub-toolbar">
        <PrismInputText
          className="hub-search"
          label="Search by name or email"
          hideLabel
          placeholder="Search by name or email"
          value={search}
          onPrismInput={(e: CustomEvent<{ value: string }>) => setSearch(e.detail?.value ?? "")}
        />
        <PrismSelect
          className="hub-status-select"
          label="Status"
          hideLabel
          value={status}
          options={statusOptions}
          onPrismChange={(e: CustomEvent<{ value: string }>) => setStatus(String(e.detail?.value ?? ALL))}
        />
      </div>

      {visible.length === 0 ? (
        <p className="waitlist-empty">
          {rows.length === 0 ? `No applications for ${year} yet.` : "No applications match these filters."}
        </p>
      ) : (
        <table className="application-rater-table waitlist-table hub-table">
          <thead>
            <tr>
              <th>Applicant</th>
              <th>Type</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Manager</th>
              <th>Ratings</th>
              <th>Average</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id}>
                <td>
                  <a href={`/application/${r.id}`} target="_blank" rel="noopener noreferrer">
                    {r.name ?? "Unknown applicant"}
                  </a>
                  {r.email && <small className="hub-email">{r.email}</small>}
                  {r.transfer_id && <span className="hub-tag">Transfer pending</span>}
                  {r.late_arrival && (
                    <button
                      type="button"
                      className="hub-tag hub-tag-action"
                      title="Arrived after the Host Review completed: accept or reject it in Host Review"
                      onClick={onOpenHostReview}
                    >
                      Decision needed
                    </button>
                  )}
                </td>
                <td>{trackLabel(r.applicant_type_id)}</td>
                <td>
                  <span className={`hub-status-pill hub-status-${statusKeyOf(r.status_id) ?? "unknown"}`}>
                    {r.status ?? "Unknown"}
                  </span>
                </td>
                <td>{formatDate(r.submitted_at)}</td>
                <td>{managerApprovalLabel(r.manager_approval_status_id)}</td>
                <td>{r.ratings}</td>
                <td>
                  {r.average === null ? (
                    <span className="waitlist-muted">—</span>
                  ) : (
                    <>
                      {r.average.toFixed(2)} <small className="waitlist-muted">{ratingCategory(r.average)}</small>
                    </>
                  )}
                </td>
                <td className="waitlist-action">
                  <PrismButton size="sm" tone="secondary" display="outline" onPrismClick={() => openManage(r)}>
                    Manage
                  </PrismButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {visible.length > 0 && (
        <div className="hub-export">
          <PrismButton size="sm" tone="secondary" display="ghost" disabled={exporting} onPrismClick={exportRows}>
            <span>{exporting ? "Exporting…" : `Export ${visible.length} row${visible.length === 1 ? "" : "s"} to XLSX`}</span>
          </PrismButton>
        </div>
      )}

      <PrismDialog
        className="hub-manage-dialog"
        open={!!managing}
        close-method="user"
        setAriaLabel="Manage application"
        onPrismClose={() => setManaging(null)}
      >
        <span slot="header">{managing ? nameOf(managing) : "Manage application"}</span>
        <div className="hub-manage">
          {managing && !undecided && (
            <PrismBanner tone="info">
              Type and chapter are locked once an application has a decision ({managing.status}).
            </PrismBanner>
          )}
          {managing && (
            <>
              <section>
              <h4>Applicant type</h4>
              <div className="hub-manage-row">
                <PrismSelect
                  label="Applicant type"
                  hideLabel
                  value={typeId}
                  options={typeOptions}
                  disabled={!undecided || busy}
                  onPrismChange={(e: CustomEvent<{ value: string }>) => setTypeId(String(e.detail?.value ?? ""))}
                />
                <PrismButton
                  disabled={!undecided || busy || Number(typeId) === managing.applicant_type_id}
                  onPrismClick={changeType}
                >
                  Save type
                </PrismButton>
              </div>
            </section>
            <section>
              <h4>Chapter transfer</h4>
              {managing.transfer_id ? (
                <p className="waitlist-muted">
                  A transfer to {chapters.find((c) => c.id === managing.transfer_to_chapter_id)?.location ?? "another chapter"}{" "}
                  is already pending.
                </p>
              ) : (
                <>
                  <PrismSelect
                    label="Move to chapter"
                    value={destination}
                    options={destinationOptions}
                    disabled={!undecided || busy}
                    onPrismChange={(e: CustomEvent<{ value: string }>) => setDestination(String(e.detail?.value ?? ""))}
                  />
                  <PrismInputTextarea
                    label="Note for the other chapter (optional)"
                    value={note}
                    maxlength={500}
                    disabled={!undecided || busy}
                    onPrismInput={(e: CustomEvent<{ value: string }>) => setNote(e.detail?.value ?? "")}
                  />
                </>
              )}
              </section>
            </>
          )}
        </div>
        <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={() => setManaging(null)}>
          Close
        </PrismButton>
        <PrismButton
          slot="footer"
          disabled={!undecided || busy || !destination || !!managing?.transfer_id}
          onPrismClick={proposeTransfer}
        >
          Propose transfer
        </PrismButton>
      </PrismDialog>
      {confirmDialog}
    </div>
  );
};

export default ApplicationsTab;
